import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { compose, withState } from 'recompose'
import { connect } from 'react-redux'
import ModalBody from 'components/Utils/Modal/Body'
import { Wrapper as ModalWrapper } from 'components/Utils/Modal/Wrapper'
import uuid from 'uuid'

import TargetForm from './TargetForm'
import UserAssociationModal from './UserAssociationModal'

import { getRawViewState } from 'data'
import { getApiEndpoint } from 'data/config/selectors'

import RoutesHelper from 'helpers/RoutesHelper'

import providesShareToApplication from '../dataSync/providesShareToApplication'
import withGetApplicationShareSettings from '../dataSync/withGetApplicationShareSettings'

import { Button } from 'components/base'

const USER_ASSOCIATION_STATUS_IDLE = 'idle'
const USER_ASSOCIATION_STATUS_PENDING = 'pending'

// This component role is to fetch the share-to settings, show the form and
// actually share the content
// It's used in the share-to editor plugin
export class TargetModal extends Component {
  static propTypes = {
    isOpen: PropTypes.bool.isRequired,
    selectedText: PropTypes.string.isRequired,
    noteId: PropTypes.string.isRequired,
    integration: PropTypes.object,
    onSuccess: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    createIntegrationTileBlock: PropTypes.func.isRequired,

    // the following come from enhanced
    shareSettingsViewState: PropTypes.object.isRequired,
    shareSettings: PropTypes.object,
    userAddUrl: PropTypes.string.isRequired,
  }

  static defaultProps = {
    onSuccess: result => {},
    onError: onError => {},
    onClose: () => {},
  }

  state = {
    shareValues: {},
    submitting: false,
    userAssociationHasBeenPerformed: false,
  }

  constructor(props) {
    super(props)

    if (props.shareSettings) {
      this.state.shareValues = getInitialValues(props.shareSettings)
    }
  }

  handleUserAssociationDone = () => {
    const {
      forceQuery,
      setUserAssociationStatus,
      performAuthenticationOnly = false,
    } = this.props

    if (performAuthenticationOnly) {
      return this.props.onClose()
    }

    setUserAssociationStatus(USER_ASSOCIATION_STATUS_PENDING)
    forceQuery()
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.shareSettings !== this.props.shareSettings) {
      const shareValues = getInitialValues(nextProps.shareSettings)
      this.setState({ shareValues })
    }
  }

  render() {
    const needsUserLinking =
      this.props.shareSettings && this.props.shareSettings.needsUserAssociation

    return (
      <ModalWrapper
        isOpen={this.props.isOpen}
        onRequestClose={this.props.onClose}
        size={needsUserLinking ? 262 : undefined}
      >
        {this.renderModalContent()}
      </ModalWrapper>
    )
  }

  renderModalContent() {
    // Fetching the share settings
    if (this.props.shareSettingsViewState.loading.size) {
      return (
        <div className="padding-large">
          <div className="shade-20">Getting share settings...</div>
        </div>
      )
    }

    // Error while fetching share settings
    if (this.props.shareSettingsViewState.errors.size) {
      return (
        <div className="padding-large">
          <div className="red">Error while loading share settings!</div>
        </div>
      )
    }

    if (!this.props.shareSettings) {
      return null
    }

    if (this.props.shareSettings.needsUserAssociation) {
      return (
        <UserAssociationModal
          onUserAssociationDone={this.handleUserAssociationDone}
          application={this.props.integration.application}
          userAddUrl={this.props.userAddUrl}
        />
      )
    }

    return (
      <ModalBody>
        <div className="font-weight-bold padding-vertical-medium font-size-largest">
          {this.props.integration.application.labels.shareToAction}
        </div>
        <TargetForm
          integration={this.props.integration}
          shareSettings={this.props.shareSettings}
          values={this.state.shareValues}
          onChange={this.onTargetFormChange}
          onSubmit={this.onSubmit}
          formProps={{
            ref: ref => {
              this.formRef = ref
            },
          }}
        >
          <div className="flexbox flexbox-space-between padding-top-medium">
            <Button.Secondary onClick={this.onCancel}>Cancel</Button.Secondary>
            <Button.Primary
              disabled={
                this.state.submitting ||
                !this.formRef ||
                !this.formRef.checkValidity()
              }
              type="submit"
              onClick={this.onSubmit}
            >
              {this.props.integration.application.labels.shareToActionVerb}
            </Button.Primary>
          </div>
        </TargetForm>
      </ModalBody>
    )
  }

  onCancel = event => {
    event.preventDefault()
    event.stopPropagation()
    this.props.onClose()
  }

  onTargetFormChange = shareValues => {
    this.setState({
      shareValues,
    })
  }

  onSubmit = () => {
    const formIsValid = this.formRef.checkValidity()
    if (!formIsValid) {
      return
    }
    this.setState({ submitting: true })
    this.props.onClose()
    let target = this.state.shareValues

    const tileTarget = this.props.shareSettings.targets.find(
      target => target.type === 'tile'
    )

    if (tileTarget) {
      const tileId = uuid()
      this.props.createIntegrationTileBlock({
        tileId,
        createdBy: this.props.integration.application.id,
        iconURL: this.props.integration.application.photoURL,
      })
      target = {
        ...target,
        [tileTarget.id]: tileId,
      }
    }

    this.props
      .shareToApplication({
        target,
        noteId: this.props.noteId,
        clientId: this.props.integration.application.clientId,
      })
      .then(data => {
        this.setState({ submitting: false })
        if (data.error) {
          this.props.onError(data.error)
        } else {
          this.props.onSuccess(data.result)
        }
      })
  }
}

export default compose(
  withState('shareSettings', 'setShareSettings', null),
  withState(
    'userAssociationStatus',
    'setUserAssociationStatus',
    USER_ASSOCIATION_STATUS_IDLE
  ),
  connect(function mapStateToProps(state, ownProps) {
    let userAddUrl = ''

    if (ownProps.shareSettings && ownProps.shareSettings.needsUserAssociation) {
      const apiEndpoint = getApiEndpoint(state)
      const application =
        (ownProps.integration && ownProps.integration.application) || {}

      userAddUrl = RoutesHelper.integrationUserAssociation({
        apiEndpoint,
        applicationClientId: application.clientId,
        onFinishedRedirectUrl: RoutesHelper.autoClosePopup(),
        associationRedirectUrl:
          ownProps.shareSettings.needsUserAssociation.redirectUrl,
      })
    }

    return {
      shareSettingsViewState: getRawViewState(state, 'loadShareSettings'),
      userAddUrl,
    }
  }),
  providesShareToApplication,
  withGetApplicationShareSettings({
    viewName: 'loadShareSettings',
    queryOnChanges: true,
    mapPropsToVariables({ integration, noteId, selectedText }) {
      return {
        noteId,
        selectedText,
        clientId: integration.application.id,
      }
    },
    onShareSettingsFetched: (
      { setShareSettings, userAssociationStatus, onClose },
      data
    ) => {
      // Means we triggered a subsequent query to check after user association
      // and we want to close the modal if the user hasn't associated his account
      if (
        userAssociationStatus === USER_ASSOCIATION_STATUS_PENDING &&
        Boolean(data.needsUserAssociation)
      ) {
        return onClose()
      }

      setShareSettings(data)
    },
  })
)(TargetModal)

function reduceTargetInitialValue(values, target) {
  const { id, initialValue } = target
  let { options } = target

  // because options can be null, defining a default value in the deconstruct above
  // won't work, so let's enforce the default empty array to avoid unnecessary shenanigans later
  options = options || []

  const base = {
    ...values,
    [id]: initialValue,
  }

  switch (options.length) {
    case 0:
      return base
    case 1:
      const selectedOption = options[0]
      const hasChildren = Boolean(selectedOption.children)
      const newValues = { ...base, [id]: selectedOption.value }

      if (hasChildren) {
        return selectedOption.children.reduce(
          reduceTargetInitialValue,
          newValues
        )
      }

      return newValues
    // * More than one option
    default: {
      const selectedOption =
        options.find(option => option.value === initialValue) || {}
      const targetValue = selectedOption.value || null
      const newValues = { ...base, [id]: targetValue }
      const hasChildren = Boolean(selectedOption.children)

      if (hasChildren) {
        return selectedOption.children.reduce(
          reduceTargetInitialValue,
          newValues
        )
      }

      return newValues
    }
  }
}

function getInitialValues(shareSettings) {
  if (!shareSettings.targets) {
    return {}
  }

  return shareSettings.targets.reduce(reduceTargetInitialValue, {})
}
